import type {
  GameCommand,
  KnownPlayerView,
  PrivatePlayerView,
  PublicPlayerView,
  PublicRoomView,
  SecretRole,
} from "@secret-cabinet/contracts";
import {
  Check,
  Eye,
  FileLock2,
  Gavel,
  LockKeyhole,
  Play,
  Search,
  ShieldCheck,
  Skull,
  Shuffle,
  UserRoundCheck,
  UserRoundX,
  Vote,
  X as XIcon,
} from "lucide-react";
import { useEffect, useState } from "react";

export type CommandDispatcher = (command: GameCommand) => Promise<void>;

export function PrivateDocket({
  room,
  player,
  dispatch,
  pending,
  error,
}: {
  room: PublicRoomView;
  player: PrivatePlayerView;
  dispatch: CommandDispatcher;
  pending: boolean;
  error: string | null;
}) {
  const [opened, setOpened] = useState(false);
  const [selection, setSelection] = useState<string | null>(null);
  const action = player.action;

  useEffect(() => {
    setOpened(false);
    setSelection(null);
  }, [room.phase, action.kind]);

  return (
    <section className="private-docket" aria-labelledby="private-docket-heading">
      <header className="private-docket__header">
        <span><FileLock2 aria-hidden="true" /> Private docket</span>
        <b>For your eyes only</b>
      </header>
      <div className="private-docket__body">
        <h2 id="private-docket-heading">{actionHeading(action.kind, room)}</h2>
        {action.kind === "lobby" ? (
          <LobbyAction ready={action.ready} pending={pending} dispatch={dispatch} room={room} />
        ) : null}
        {action.kind === "reveal-role" ? (
          <RoleReveal
            room={room}
            player={player}
            opened={opened}
            setOpened={setOpened}
            pending={pending}
            dispatch={dispatch}
          />
        ) : null}
        {action.kind === "nominate" ? (
          <PlayerSelection
            players={room.players}
            eligibleIds={action.eligiblePlayerIds}
            selectedId={selection}
            setSelectedId={setSelection}
            pending={pending}
            confirmLabel="Confirm nomination"
            onConfirm={(playerId) => dispatch({ type: "nominate", playerId })}
          />
        ) : null}
        {action.kind === "vote" ? (
          <VoteAction ballot={action.ballot} pending={pending} dispatch={dispatch} />
        ) : null}
        {action.kind === "discard-policy" ? (
          <PolicyAction
            action={action}
            selectedId={selection}
            setSelectedId={setSelection}
            pending={pending}
            dispatch={dispatch}
          />
        ) : null}
        {action.kind === "respond-veto" ? (
          <VetoResponse pending={pending} dispatch={dispatch} />
        ) : null}
        {action.kind === "select-player" ? (
          <PlayerSelection
            players={room.players}
            eligibleIds={action.eligiblePlayerIds}
            selectedId={selection}
            setSelectedId={setSelection}
            pending={pending}
            danger={action.power === "execution"}
            power={action.power}
            confirmLabel={powerConfirmLabel(action.power)}
            onConfirm={(playerId) => dispatch({ type: "select-player", playerId })}
            intro={powerIntro(action.power)}
          />
        ) : null}
        {action.kind === "investigation-result" ? (
          <InvestigationResult
            target={room.players.find((candidate) => candidate.id === action.playerId)}
            membership={action.membership}
            pending={pending}
            dispatch={dispatch}
          />
        ) : null}
        {action.kind === "policy-peek" ? (
          <PolicyPeek cards={action.cards} pending={pending} dispatch={dispatch} />
        ) : null}
        {action.kind === "observe" ? (
          <ObserveAction
            reason={action.reason}
            canResume={action.canResume ?? false}
            room={room}
            pending={pending}
            dispatch={dispatch}
          />
        ) : null}

        {error ? <p className="action-error" role="alert">{error}</p> : null}
        {pending ? <p className="action-pending" aria-live="polite">Submitting to the chamber…</p> : null}
      </div>
      {action.kind !== "reveal-role" && player.role ? (
        <RoleReminder role={player.role} knownPlayers={player.knownPlayers} room={room} />
      ) : null}
    </section>
  );
}

function LobbyAction({
  ready,
  pending,
  dispatch,
  room,
}: {
  ready: boolean;
  pending: boolean;
  dispatch: CommandDispatcher;
  room: PublicRoomView;
}) {
  const readyCount = room.players.filter((candidate) => candidate.ready).length;
  return (
    <div className="docket-action">
      <p>
        The game starts automatically when 5–10 connected players have marked themselves ready.
      </p>
      <p className="ledger-line"><span>Ready now</span><b>{readyCount} / {room.players.length}</b></p>
      <button
        className={`button button--wide ${ready ? "button--mint" : "button--primary"}`}
        type="button"
        disabled={pending}
        onClick={() => void dispatch({ type: "set-ready", ready: !ready })}
      >
        {ready ? <Check aria-hidden="true" /> : <UserRoundCheck aria-hidden="true" />}
        {ready ? "Ready — tap to withdraw" : "I am ready"}
      </button>
    </div>
  );
}

function RoleReveal({
  room,
  player,
  opened,
  setOpened,
  pending,
  dispatch,
}: {
  room: PublicRoomView;
  player: PrivatePlayerView;
  opened: boolean;
  setOpened: (opened: boolean) => void;
  pending: boolean;
  dispatch: CommandDispatcher;
}) {
  if (!opened) {
    return (
      <div className="sealed-dossier">
        <LockKeyhole aria-hidden="true" />
        <p>Keep your screen private before breaking the seal.</p>
        <button className="button button--primary button--wide" type="button" onClick={() => setOpened(true)}>
          <Eye aria-hidden="true" /> Reveal my role
        </button>
      </div>
    );
  }
  if (!player.role) {
    return <p>Your role dossier is unavailable.</p>;
  }
  return (
    <div className={`role-dossier role-dossier--${roleParty(player.role)}`}>
      <p className="eyebrow">Your secret role</p>
      <h3>{roleName(player.role)}</h3>
      <p>{roleObjective(player.role)}</p>
      <KnownPlayers knownPlayers={player.knownPlayers} room={room} role={player.role} />
      <button
        className="button button--ink button--wide"
        type="button"
        disabled={pending}
        onClick={() => void dispatch({ type: "acknowledge-role" })}
      >
        <ShieldCheck aria-hidden="true" /> Seal dossier and continue
      </button>
    </div>
  );
}

function VoteAction({
  ballot,
  pending,
  dispatch,
}: {
  ballot: "ja" | "nein" | null;
  pending: boolean;
  dispatch: CommandDispatcher;
}) {
  if (ballot) {
    return (
      <div className="vote-locked">
        <Vote aria-hidden="true" />
        <p>Your ballot is locked.</p>
        <strong>{ballot.toUpperCase()}</strong>
      </div>
    );
  }
  return (
    <div className="ballot-actions">
      <p>Approve or reject the proposed President and Chancellor together.</p>
      <button
        className="ballot ballot--ja"
        type="button"
        disabled={pending}
        onClick={() => void dispatch({ type: "vote", ballot: "ja" })}
      >
        <small>Vote</small><strong>JA</strong><span>Approve</span>
      </button>
      <button
        className="ballot ballot--nein"
        type="button"
        disabled={pending}
        onClick={() => void dispatch({ type: "vote", ballot: "nein" })}
      >
        <small>Vote</small><strong>NEIN</strong><span>Reject</span>
      </button>
    </div>
  );
}

function PolicyAction({
  action,
  selectedId,
  setSelectedId,
  pending,
  dispatch,
}: {
  action: Extract<PrivatePlayerView["action"], { kind: "discard-policy" }>;
  selectedId: string | null;
  setSelectedId: (id: string | null) => void;
  pending: boolean;
  dispatch: CommandDispatcher;
}) {
  const isPresident = action.office === "president";
  const selectedCard = action.cards.find((card) => card.id === selectedId);
  const chancellorDiscardId = !isPresident && selectedCard
    ? action.cards.find((card) => card.id !== selectedCard.id)?.id ?? null
    : null;
  return (
    <div className="docket-action">
      <p>
        {isPresident
          ? "Choose one policy to discard. The other two pass privately to the Chancellor."
          : "Choose one policy to enact. The other policy will be discarded in secret."} Keep your screen private.
      </p>
      <div className="policy-hand">
        {action.cards.map((card, index) => (
          <button
            className={`policy-paper policy-paper--${card.type}${selectedId === card.id ? " is-selected" : ""}${chancellorDiscardId === card.id ? " is-discarded" : ""}`}
            type="button"
            key={card.id}
            disabled={pending}
            aria-pressed={selectedId === card.id}
            onClick={() => setSelectedId(selectedId === card.id ? null : card.id)}
          >
            <small>Policy {String(index + 1).padStart(2, "0")}</small>
            <strong>{capitalise(card.type)}</strong>
            {isPresident && selectedId === card.id ? <XIcon className="policy-paper__cross" aria-hidden="true" /> : null}
            {!isPresident && chancellorDiscardId === card.id ? <XIcon className="policy-paper__cross" aria-hidden="true" /> : null}
            <span>
              {isPresident
                ? selectedId === card.id ? "Discarding this card" : "Tap to discard"
                : selectedId === card.id ? "Enacting this card" : chancellorDiscardId === card.id ? "Will be discarded" : "Tap to enact"}
            </span>
          </button>
        ))}
      </div>
      <button
        className="button button--ink button--wide"
        type="button"
        disabled={!selectedId || pending}
        onClick={() => {
          const cardId = isPresident ? selectedId : chancellorDiscardId;
          if (cardId) void dispatch({ type: "discard-policy", cardId });
        }}
      >
        <Gavel aria-hidden="true" /> {isPresident ? "Confirm secret discard" : "Enact selected policy"}
      </button>
      {action.vetoAvailable ? (
        <button
          className="button button--quiet button--wide"
          type="button"
          disabled={pending}
          onClick={() => void dispatch({ type: "request-veto" })}
        >
          Request a veto instead
        </button>
      ) : null}
      {action.vetoRejected ? <p className="field-note">The President rejected the veto. A policy must be enacted.</p> : null}
    </div>
  );
}

function VetoResponse({ pending, dispatch }: { pending: boolean; dispatch: CommandDispatcher }) {
  return (
    <div className="docket-action">
      <p>The Chancellor wants to discard both policies. An accepted veto advances the election tracker.</p>
      <div className="decision-pair">
        <button
          className="button button--mint"
          type="button"
          disabled={pending}
          onClick={() => void dispatch({ type: "respond-veto", accept: true })}
        >
          Accept veto
        </button>
        <button
          className="button button--quiet"
          type="button"
          disabled={pending}
          onClick={() => void dispatch({ type: "respond-veto", accept: false })}
        >
          Reject veto
        </button>
      </div>
    </div>
  );
}

function PlayerSelection({
  players,
  eligibleIds,
  selectedId,
  setSelectedId,
  pending,
  confirmLabel,
  onConfirm,
  intro = "Choose one eligible player, then confirm your decision.",
  danger = false,
  power,
}: {
  players: PublicPlayerView[];
  eligibleIds: string[];
  selectedId: string | null;
  setSelectedId: (id: string | null) => void;
  pending: boolean;
  confirmLabel: string;
  onConfirm: (playerId: string) => Promise<void>;
  intro?: string;
  danger?: boolean;
  power?: "investigate" | "special-election" | "execution";
}) {
  return (
    <div className={`docket-action power-action${power ? ` power-action--${power}` : ""}`}>
      {power ? <PowerGlyph power={power} /> : null}
      <p>{intro}</p>
      <div className="selection-register">
        {players.filter((candidate) => eligibleIds.includes(candidate.id)).map((candidate) => (
          <button
            type="button"
            key={candidate.id}
            className={selectedId === candidate.id ? "is-selected" : ""}
            aria-pressed={selectedId === candidate.id}
            disabled={pending}
            onClick={() => setSelectedId(selectedId === candidate.id ? null : candidate.id)}
          >
            <span>{String(candidate.seat).padStart(2, "0")}</span>
            <strong>{candidate.name}</strong>
            {selectedId === candidate.id ? <Check aria-hidden="true" /> : null}
          </button>
        ))}
      </div>
      <button
        className={`button button--wide ${danger ? "button--danger" : "button--ink"}`}
        type="button"
        disabled={!selectedId || pending}
        onClick={() => selectedId && void onConfirm(selectedId)}
      >
        {danger ? <UserRoundX aria-hidden="true" /> : <UserRoundCheck aria-hidden="true" />}
        {confirmLabel}
      </button>
    </div>
  );
}

function InvestigationResult({
  target,
  membership,
  pending,
  dispatch,
}: {
  target: PublicPlayerView | undefined;
  membership: "liberal" | "fascist";
  pending: boolean;
  dispatch: CommandDispatcher;
}) {
  return (
    <div className={`investigation-result investigation-result--${membership}`}>
      <Eye aria-hidden="true" />
      <small>Party membership — not secret role</small>
      <h3>{target?.name ?? "Selected player"}</h3>
      <strong>{capitalise(membership)}</strong>
      <p>You may tell the room the truth or lie. The app will not publish this result.</p>
      <button
        className="button button--ink button--wide"
        type="button"
        disabled={pending}
        onClick={() => void dispatch({ type: "acknowledge-power" })}
      >
        Close confidential result
      </button>
    </div>
  );
}

function PolicyPeek({
  cards,
  pending,
  dispatch,
}: {
  cards: Array<{ id: string; type: "liberal" | "fascist" }>;
  pending: boolean;
  dispatch: CommandDispatcher;
}) {
  return (
    <div className="docket-action">
      <p>These are the next three policies in order. Do not rearrange them.</p>
      <div className="policy-hand policy-hand--peek">
        {cards.map((card, index) => (
          <div className={`policy-paper policy-paper--${card.type}`} key={card.id}>
            <small>Next {index + 1}</small>
            <strong>{capitalise(card.type)}</strong>
          </div>
        ))}
      </div>
      <button
        className="button button--ink button--wide"
        type="button"
        disabled={pending}
        onClick={() => void dispatch({ type: "acknowledge-power" })}
      >
        Close policy forecast
      </button>
    </div>
  );
}

function ObserveAction({
  reason,
  room,
  canResume = false,
  pending,
  dispatch,
}: {
  reason: "waiting" | "executed" | "paused" | "game-over";
  room: PublicRoomView;
  canResume?: boolean;
  pending: boolean;
  dispatch: CommandDispatcher;
}) {
  const copy = {
    waiting: "Your decision is not required right now. Debate with the room and watch the public chamber.",
    executed: "You have been executed. You may watch silently, but may not speak, vote, or reveal your role.",
    paused: room.phaseInstruction,
    "game-over": "The final roles are now published in the attendance register.",
  }[reason];
  return (
    <div className="observe-state">
      {reason === "executed" ? <Skull className="observe-state__skull" aria-hidden="true" /> : <ShieldCheck aria-hidden="true" />}
      <p>{copy}</p>
      {reason === "paused" && canResume && room.pause.resumeAt === null ? (
        <button
          className="button button--primary button--wide"
          type="button"
          disabled={pending}
          onClick={() => void dispatch({ type: "resume" })}
        >
          <Play aria-hidden="true" /> Resume chamber
        </button>
      ) : null}
    </div>
  );
}

function RoleReminder({
  role,
  knownPlayers,
  room,
}: {
  role: SecretRole;
  knownPlayers: KnownPlayerView[];
  room: PublicRoomView;
}) {
  return (
    <details className="role-reminder">
      <summary><Eye aria-hidden="true" /> Privately reveal my dossier</summary>
      <div className={`role-reminder__inside role-dossier--${roleParty(role)}`}>
        <small>Your role</small>
        <strong>{roleName(role)}</strong>
        <p>{roleObjective(role)}</p>
        <KnownPlayers knownPlayers={knownPlayers} room={room} role={role} />
      </div>
    </details>
  );
}

function KnownPlayers({
  knownPlayers,
  room,
  role,
}: {
  knownPlayers: KnownPlayerView[];
  room: PublicRoomView;
  role: SecretRole;
}) {
  if (knownPlayers.length === 0) {
    return (
      <p className="known-players">
        {role === "hitler" && room.players.length >= 7
          ? "In this large game, Hitler does not know the Fascists."
          : "No other player is identified to you."}
      </p>
    );
  }
  return (
    <div className="known-players">
      <small>Known to you</small>
      {knownPlayers.map((known) => {
        const publicPlayer = room.players.find((candidate) => candidate.id === known.playerId);
        return (
          <p key={known.playerId}>
            <b>{publicPlayer?.name ?? "Unknown player"}</b>
            <span>{roleName(known.role)}</span>
          </p>
        );
      })}
    </div>
  );
}

function actionHeading(kind: PrivatePlayerView["action"]["kind"], room: PublicRoomView): string {
  switch (kind) {
    case "lobby": return "Confirm your attendance";
    case "reveal-role": return "Open your private dossier";
    case "nominate": return "Choose a Chancellor";
    case "vote": return "Cast your secret ballot";
    case "discard-policy": return room.phase === "legislative-president" ? "President: discard one of three" : "Chancellor: enact one of two";
    case "respond-veto": return "Answer the veto request";
    case "select-player": return "Use Presidential power";
    case "investigation-result": return "Read the confidential finding";
    case "policy-peek": return "Review the policy forecast";
    case "observe": return "Watch the chamber";
  }
}

function roleName(role: SecretRole): string {
  return role === "hitler" ? "Hitler" : capitalise(role);
}

function roleParty(role: SecretRole): "liberal" | "fascist" {
  return role === "liberal" ? "liberal" : "fascist";
}

function roleObjective(role: SecretRole): string {
  if (role === "liberal") {
    return "Enact five Liberal policies or help the President execute Hitler.";
  }
  if (role === "fascist") {
    return "Protect Hitler and secure six Fascist policies—or elect Hitler Chancellor after three.";
  }
  return "Stay hidden. Win by becoming Chancellor after three Fascist policies, or by reaching six policies.";
}

function powerIntro(power: "investigate" | "special-election" | "execution"): string {
  if (power === "investigate") return "Privately inspect one player's party membership, not their exact role.";
  if (power === "special-election") return "Choose the next Presidential candidate. Normal order resumes afterward.";
  return "Execution is final. If the target is Hitler, the Liberals win immediately.";
}

function powerConfirmLabel(power: "investigate" | "special-election" | "execution"): string {
  if (power === "investigate") return "Confirm investigation";
  if (power === "special-election") return "Call special election";
  return "Confirm execution";
}

function PowerGlyph({ power }: { power: "investigate" | "special-election" | "execution" }) {
  if (power === "investigate") return <Search className="power-action__icon" aria-hidden="true" />;
  if (power === "special-election") return <Shuffle className="power-action__icon" aria-hidden="true" />;
  return <Skull className="power-action__icon" aria-hidden="true" />;
}

function capitalise(value: string): string {
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
}
